import React, { useState, useEffect } from 'react';
import { getAvatarSrc } from '../../utils/avatarConstants';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Alert,
  Avatar,
  Chip,
  Stack,
  Divider,
  ButtonGroup,
  Paper
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Email as EmailIcon, 
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { formatDistanceToNow } from 'date-fns';
import useThemeStyles from '../../theme/useThemeStyles';

function Share() {
  const styles = useThemeStyles();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('partner');
  const [users, setUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [weddingCode, setWeddingCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchWeddingCode();
    fetchAccessRequests();
  }, []);

  const fetchWeddingCode = async () => {
    try {
      const response = await api.get('/api/wedding/access-code');
      setWeddingCode(response.data.accessCode);
    } catch (error) {}
  };

  const fetchAccessRequests = async () => {
    try {
      const response = await api.get('/api/wedding/access-requests');
      setAccessRequests(response.data.requests || []);
    } catch (error) {}
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/users/wedding-users');

      if (response.data.users) {
        const formattedUsers = response.data.users.map(weddingUser => ({
          _id: weddingUser.user._id,
          firstName: weddingUser.user.firstName,
          lastName: weddingUser.user.lastName,
          email: weddingUser.user.email,
          profilePicture: weddingUser.user.profilePicture,
          role: weddingUser.role
        }));
        setUsers(formattedUsers);
      }

      if (response.data.pendingInvitations) {
        setPendingInvitations(response.data.pendingInvitations);
      } else {}
    } catch (error) {
      setError('Failed to fetch users');
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/wedding/send-access-code', { email });
      setSuccess('Access code sent successfully!');
      setEmail('');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send access code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(weddingCode);
    setSuccess('Access code copied to clipboard!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleRegenerateCode = async () => {
    try {
      const response = await api.post('/api/wedding/regenerate-access-code');
      setWeddingCode(response.data.accessCode);
      setSuccess('New access code generated successfully!');
    } catch (error) {
      setError('Failed to regenerate access code');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/api/wedding/share', { email, role });

      setSuccess('Invitation sent successfully');
      setEmail('');

      // Only add to pending if the user doesn't already exist
      if (response.data.status === 'pending') {
        const newInvitation = {
          email,
          role,
          createdAt: new Date().toISOString(),
        };
        setPendingInvitations(prev => {
          const updated = [...prev, newInvitation];
          return updated;
        });
      }

      // Refresh the lists
      await fetchUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await api.delete(`/api/wedding/users/${userId}`);
      fetchUsers();
    } catch (error) {
      setError('Failed to remove user');
    }
  };

  const handleCancelInvitation = async (email) => {
    try {
      await api.delete(`/api/wedding/invitations/${email}`);
      setPendingInvitations(prev => prev.filter(invite => invite.email !== email));
      setSuccess('Invitation cancelled successfully');
    } catch (error) {
      setError('Failed to cancel invitation');
    }
  };

  const handleAcceptInvitation = async (email) => {
    try {
      await api.post('/api/wedding/accept-invitation', { email });
      setSuccess('Invitation accepted successfully');
      fetchUsers(); // Refresh the lists
    } catch (error) {
      setError('Failed to accept invitation');
    }
  };

  const handleAccessRequest = async (requestId, action) => {
    try {
      await api.post(`/api/wedding/respond-access-request`, {
        requestId,
        action,
        message: action === 'approved' ? 'Access granted!' : 'Access denied.'
      });
      setSuccess(`Access request ${action} successfully`);
      fetchAccessRequests();
    } catch (error) {
      setError(`Failed to ${action} access request`);
    }
  };

  useEffect(() => {}, [pendingInvitations]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 3 }}>
      {/* Wedding Access Code Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, display: 'flex', alignItems: 'center', mb: 3 }}>
            <ShareIcon sx={{ mr: 1 }} />
            Wedding Access Code
          </Typography>
          
          <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
            <Typography variant="h6" gutterBottom>
              Share this code with suppliers or guests:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {weddingCode}
              </Typography>
              <IconButton onClick={handleCopyCode} sx={{ color: 'primary.contrastText' }}>
                <CopyIcon />
              </IconButton>
            </Box>
            <Button 
              variant="outlined" 
              onClick={handleRegenerateCode}
              sx={{ mt: 2, color: 'primary.contrastText', borderColor: 'primary.contrastText' }}
            >
              Generate New Code
            </Button>
          </Paper>

          {/* Send Code via Email */}
          <Typography variant="h6" gutterBottom>
            Send Code via Email
          </Typography>
          <form onSubmit={handleSendCode}>
            <Stack direction="row" spacing={2} alignItems="flex-end">
              <TextField
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email to send access code"
              />
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={loading || !email.trim()}
                startIcon={<EmailIcon />}
              >
                {loading ? 'Sending...' : 'Send Code'}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      {/* Access Requests Section */}
      {accessRequests.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonAddIcon sx={{ mr: 1 }} />
              Access Requests ({accessRequests.length})
            </Typography>
            
            <Stack spacing={2}>
              {accessRequests.map((request) => (
                <Paper key={request._id} elevation={1} sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="subtitle1">
                        {request.user?.firstName} {request.user?.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {request.user?.email} • {request.role}
                      </Typography>
                      {request.message && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          "{request.message}"
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Requested {formatDistanceToNow(new Date(request.requestedAt))} ago
                      </Typography>
                    </Box>
                    <ButtonGroup size="small">
                      <Button
                        onClick={() => handleAccessRequest(request._id, 'approved')}
                        color="success"
                        variant="outlined"
                      >
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleAccessRequest(request._id, 'rejected')}
                        color="error"
                        variant="outlined"
                      >
                        Reject
                      </Button>
                    </ButtonGroup>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Existing Invitation System */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Direct Invitations
          </Typography>
          
          <form onSubmit={handleInvite}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <TextField
                select
                fullWidth
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="family">Family</MenuItem>
                <MenuItem value="friend">Friend</MenuItem>
              </TextField>
              <Button type="submit" variant="contained" color="primary">
                Send Invitation
              </Button>
            </Stack>
          </form>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          {users.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Current Users
              </Typography>
              <Stack spacing={2}>
                {users.map((user) => (
                  <Box
                    key={user._id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 1
                    }}
                  >
                    <Avatar src={getAvatarSrc(user.profilePicture)} sx={{ mr: 2 }}>
                      {user.firstName?.[0]}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography>{`${user.firstName} ${user.lastName}`}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Chip 
                      size="small" 
                      label={user.role}
                      sx={{ mr: 1 }}
                    />
                    <IconButton 
                      onClick={() => handleRemoveUser(user._id)}
                      disabled={user.role === 'owner'}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </>
          )}

          {users.length > 0 && pendingInvitations.length > 0 && (
            <Divider sx={{ my: 4 }} />
          )}

          {pendingInvitations.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
                Pending Invitations
              </Typography>
              <Stack spacing={2}>
                {pendingInvitations.map((invite) => (
                  <Box
                    key={invite.email}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 1
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography>{invite.email}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Invited {formatDistanceToNow(new Date(invite.createdAt))} ago
                      </Typography>
                    </Box>
                    <Chip 
                      size="small" 
                      label={invite.role}
                      sx={{ mr: 1 }}
                    />
                    <ButtonGroup size="small">
                      {localStorage.getItem('userEmail') === invite.email && (
                        <Button
                          onClick={() => handleAcceptInvitation(invite.email)}
                          startIcon={<CheckIcon />}
                          color="success"
                        >
                          Accept
                        </Button>
                      )}
                      <Button
                        onClick={() => handleCancelInvitation(invite.email)}
                        startIcon={<DeleteIcon />}
                        color="error"
                      >
                        Cancel
                      </Button>
                    </ButtonGroup>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Share; 